import { ReactElement } from 'react';
import { Chip, Tooltip } from '@mui/material';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import TaskAltIcon from '@mui/icons-material/TaskAlt';

import { EsgRecord } from '../../../types/syntiro_honey';

/**
 * Mapping trust is distinguished by GLYPH, not hue.
 *
 * It used to be a coloured chip, which put a green "Exact"/amber "AI-suggested"
 * beside an equally green/amber confidence chip and status chip — three colours in
 * one row meaning three unrelated things. Worse, green marked both states and the
 * confirm ACTION, so a verb looked like a noun. Confidence is now the row's only
 * traffic light; mapping trust reads from its icon and label, which carry the
 * distinction without competing for the same channel.
 */
/**
 * Labels are SHORT because the chip shares one grid line with the confidence score
 * and the confirm action; the full sentence lives in the tooltip, which is also the
 * chip's accessible name, so nothing is lost by shortening the visible text.
 */
const MAPPING_DISPLAY: Record<
  EsgRecord['mappingStatus'],
  { label: string; title: string; icon: ReactElement }
> = {
  EXACT: {
    label: 'Exact',
    title: 'Exact metric match — the label matched the catalogue deterministically',
    icon: <TaskAltIcon />
  },
  SUGGESTED: {
    label: 'AI-suggested',
    title: 'Suggested by the model — confirm it to make it a human-verified mapping',
    icon: <AutoAwesomeOutlinedIcon />
  },
  UNMAPPED: {
    label: 'Unmapped',
    title: 'No metric resolved — pick one to complete this record',
    icon: <HelpOutlineIcon />
  }
};

interface MappingStatusChipProps {
  /** The mapping trust status to render (separate from the value's trust). */
  status: EsgRecord['mappingStatus'];
  /**
   * Whether the record is approved, in which case the mapping reads as settled
   * ("Confirmed") regardless of how it was originally derived.
   */
  approved?: boolean;
  /**
   * Whether the metric came from a learned rule (the per-client alias loop), which
   * is worth naming: it was the team's own prior decision, not an AI guess.
   */
  learned?: boolean;
}

/**
 * Chip naming how the record's metric was arrived at — the mapping half of the
 * two-signal split (DECISIONS #38): this answers "is the metric right", while the
 * confidence chip answers "is the whole record right". Both remain separately
 * readable; only their colour treatment differs.
 * @param {MappingStatusChipProps} props The mapping status and its provenance flags.
 * @returns {JSX.Element}
 */
export default function MappingStatusChip({
  status,
  approved = false,
  learned = false
}: MappingStatusChipProps) {
  // Approval outranks provenance: a settled record reads "Confirmed" even when the
  // mapping originally came from a learned rule.
  const display =
    approved && status !== 'UNMAPPED'
      ? {
          label: 'Confirmed',
          title: 'Confirmed — a reviewer approved this mapping',
          icon: <TaskAltIcon />
        }
      : learned && status === 'EXACT'
        ? {
            label: 'Learned',
            title: 'Mapped automatically from a rule your team taught earlier',
            icon: <SchoolOutlinedIcon />
          }
        : MAPPING_DISPLAY[status];
  return (
    <Tooltip title={display.title}>
      <Chip
        label={display.label}
        icon={display.icon}
        size="small"
        variant="outlined"
        // A bare Chip is a roleless div: without this the tooltip text never
        // reaches assistive tech and the glyph carries the meaning alone.
        role="img"
        aria-label={display.title}
      />
    </Tooltip>
  );
}
